import React from 'react';
import styled, { css } from 'styled-components';
import { Plus16 } from '@honeybook/icons';
import HBText, { TAllColorKeys } from '../HBText/HBText';
import colors, { TColorKeys } from '../../styles/colors';
import { vars } from '../../theme';

interface Props {
    value: string;
    dropdownNoOptionsExplanation?: string;
    noOptionsButtonText?: string;
    showPlusIcon?: boolean;
    /** Legacy colors.ts key or semantic text.* key for the add action. */
    color?: TColorKeys | TAllColorKeys;
}

const resolveNoOptionsColor = (
    color?: TColorKeys | TAllColorKeys
): { text: TAllColorKeys; icon: string } => {
    if (!color) {
        return {
            text: 'text.accent',
            icon: vars.colors.text.accent
        };
    }
    if (typeof color === 'string' && color.startsWith('text.')) {
        const semanticKey = color.slice(5) as keyof typeof vars.colors.text;
        return {
            text: color as TAllColorKeys,
            icon: vars.colors.text[semanticKey] || vars.colors.text.accent
        };
    }
    return {
        text: color as TAllColorKeys,
        icon: colors[color as TColorKeys]
    };
};

const HBDropdownNoOptions = ({
    value,
    dropdownNoOptionsExplanation,
    noOptionsButtonText = 'Add',
    showPlusIcon = true,
    color
}: Props) => {
    const resolved = resolveNoOptionsColor(color);

    return (
        <DropdownNoOptionsContainer
            title=""
            hasExplanationText={!!dropdownNoOptionsExplanation}
        >
            {dropdownNoOptionsExplanation && (
                <DropdownNoOptionsTextWrapper>
                    <HBText type="body12" color="text.secondary">
                        {dropdownNoOptionsExplanation}
                    </HBText>
                </DropdownNoOptionsTextWrapper>
            )}

            <DropdownNoOptionsAddButton>
                {showPlusIcon && <Plus16 color={resolved.icon} />}
                <DropdownNoOptionsAddButtonText
                    type="emphasis14"
                    color={resolved.text}
                >
                    {!!value
                        ? `${noOptionsButtonText} "${value}"`
                        : noOptionsButtonText}
                </DropdownNoOptionsAddButtonText>
            </DropdownNoOptionsAddButton>
        </DropdownNoOptionsContainer>
    );
};

export default HBDropdownNoOptions;

export const DropdownNoOptionsTextWrapper = styled.div`
    padding: 7px 0 7px 3px;
    white-space: break-spaces;
    line-height: 15px;
`;

export const DropdownNoOptionsAddButton = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;

    svg {
        display: flex;
        flex-shrink: 0;
    }
`;

export const DropdownNoOptionsAddButtonText = styled(HBText)`
    margin-left: 8px;
    display: flex;
    align-items: center;
`;

export const DropdownNoOptionsContainer = styled.div<{
    hasExplanationText: boolean;
}>`
    ${({ hasExplanationText }) =>
        hasExplanationText &&
        css`
            ${DropdownNoOptionsAddButton} {
                padding: 2px 5px 10px;
            }
        `}
`;
